import { useEffect, useRef, useState } from 'react';
import { Typography, Spin, Pagination, Input } from 'antd';
import { ProductScoreDto } from 'interfaces/apiTypes';
import { Subtitle } from 'components/typography';
import { ProductRankingCard } from './productRankingCard';

interface ProductRankingListProps {
  pinnedItems: ProductScoreDto[];
  rankedItems: ProductScoreDto[];
  loading?: boolean;
  rankingPage: number;
  rankingPageSize: number;
  rankingTotal: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onRankingPageChange: (page: number, pageSize: number) => void;
  onPin: (productScoreId: number) => void;
  onUnpin: (productScoreId: number) => void;
  onReorderPinned: (productScoreIds: number[]) => void;
}

const PIN_HEADER = {
  background: '#fffbe6',
  border: '#ffe58f',
  heading: '#874d00',
  secondary: '#ad6800',
} as const;

const reorder = <T,>(items: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items;
  }
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

const MIDPOINT_DEAD_ZONE_PX = 14;

const resolveDropIndex = (
  from: number,
  overIndex: number,
  clientY: number,
  itemTop: number,
  itemHeight: number,
  previousDropIndex: number | null,
): number => {
  if (from === overIndex) {
    return from;
  }

  const mid = itemTop + itemHeight / 2;
  let placeAfter: boolean;
  if (clientY < mid - MIDPOINT_DEAD_ZONE_PX) {
    placeAfter = false;
  } else if (clientY > mid + MIDPOINT_DEAD_ZONE_PX) {
    placeAfter = true;
  } else if (previousDropIndex != null) {
    return previousDropIndex;
  } else {
    placeAfter = clientY >= mid;
  }

  let insertBefore = placeAfter ? overIndex + 1 : overIndex;
  if (from < insertBefore) {
    insertBefore -= 1;
  }
  return insertBefore;
};

export const ProductRankingList = ({
  pinnedItems,
  rankedItems,
  loading,
  rankingPage,
  rankingPageSize,
  rankingTotal,
  searchValue,
  onSearchChange,
  onRankingPageChange,
  onPin,
  onUnpin,
  onReorderPinned,
}: ProductRankingListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [rankingPage, rankingPageSize]);

  const draggedItem =
    dragIndex !== null && dragIndex >= 0 && dragIndex < pinnedItems.length
      ? pinnedItems[dragIndex]
      : null;

  const commitMove = (from: number, to: number) => {
    if (to < 0 || to >= pinnedItems.length || from === to) return;
    onReorderPinned(reorder(pinnedItems, from, to).map((x) => x.id));
  };

  const clearDrag = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  const onDragStart = (index: number, e: React.DragEvent) => {
    setDragIndex(index);
    setDropIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const onDragOverItem = (overIndex: number, e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragIndex === null) return;
    if (overIndex === dragIndex) return;

    const rect = e.currentTarget.getBoundingClientRect();
    setDropIndex((prev) => {
      const next = resolveDropIndex(
        dragIndex,
        overIndex,
        e.clientY,
        rect.top,
        rect.height,
        prev,
      );
      return prev === next ? prev : next;
    });
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIndex !== null && dropIndex !== null) {
      commitMove(dragIndex, dropIndex);
    }
    clearDrag();
  };

  const isDragging = dragIndex !== null;
  const willMove =
    isDragging && dropIndex !== null && dragIndex !== dropIndex && draggedItem != null;

  const lineBeforeIndex = (() => {
    if (!willMove || dragIndex === null || dropIndex === null) return null;
    if (dropIndex < dragIndex) return dropIndex;
    return dropIndex + 1;
  })();

  const dropLineFor = (index: number): 'above' | 'below' | null => {
    if (lineBeforeIndex === index) return 'above';
    if (lineBeforeIndex === pinnedItems.length && index === pinnedItems.length - 1) {
      return 'below';
    }
    return null;
  };

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        width: '100%',
        padding: '1rem',
        maxHeight: 'calc(100vh - 280px)',
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
      }}
    >
      <Input.Search
        placeholder="Sök efter produkt..."
        allowClear
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        style={{ width: '100%', marginBottom: '1rem', flexShrink: 0 }}
      />
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        <Spin spinning={loading}>
          <div
            style={{
              background: PIN_HEADER.background,
              border: `1px solid ${PIN_HEADER.border}`,
              borderRadius: 8,
              marginBottom: '1.25rem',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '0.75rem 1rem 0.5rem' }}>
              <Typography.Title
                level={5}
                style={{ margin: 0, color: PIN_HEADER.heading }}
              >
                Fastnålade produkter · {pinnedItems.length}
              </Typography.Title>
              <Typography.Text
                style={{
                  display: 'block',
                  marginBottom: willMove ? 8 : 4,
                  color: PIN_HEADER.secondary,
                  fontSize: 12,
                }}
              >
                Ligger alltid först. Dra för att ändra ordning.
              </Typography.Text>

              {willMove && (
                <div
                  style={{
                    marginBottom: 8,
                    padding: '8px 12px',
                    borderRadius: 6,
                    background: '#fff8e1',
                    border: `1px dashed ${PIN_HEADER.border}`,
                    color: PIN_HEADER.heading,
                    fontSize: 13,
                  }}
                >
                  Flyttar <strong>{draggedItem.name}</strong>
                  {' → plats '}
                  <strong>#{(dropIndex ?? 0) + 1}</strong>
                </div>
              )}
            </div>

            {pinnedItems.length === 0 ? (
              <div
                style={{
                  padding: '1.25rem 1rem',
                  textAlign: 'center',
                  color: PIN_HEADER.secondary,
                  fontSize: 13,
                  borderTop: `1px solid ${PIN_HEADER.border}`,
                }}
              >
                Inga fastnålade produkter
              </div>
            ) : (
              pinnedItems.map((item, index) => (
                <ProductRankingCard
                  key={item.id}
                  item={item}
                  rank={index + 1}
                  variant="pinned"
                  isDragging={dragIndex === index}
                  dropLine={dropLineFor(index)}
                  canMoveUp={index > 0}
                  canMoveDown={index < pinnedItems.length - 1}
                  onUnpin={onUnpin}
                  onMoveUp={() => commitMove(index, index - 1)}
                  onMoveDown={() => commitMove(index, index + 1)}
                  draggable
                  onDragStart={(e) => onDragStart(index, e)}
                  onDragOver={(e) => onDragOverItem(index, e)}
                  onDrop={onDrop}
                  onDragEnd={clearDrag}
                />
              ))
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 8,
            }}
          >
            <Subtitle>
              Rankade av regler · {rankingTotal}
            </Subtitle>
            <span
              style={{
                fontSize: 12,
                color: '#8c8c8c',
                flex: 'none',
              }}
            >
              Sorterat på rankingpoäng
            </span>
          </div>

          <div
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              overflow: 'hidden',
              background: '#fff',
              marginBottom: '0.5rem',
            }}
          >
            {rankedItems.length === 0 ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', color: '#8c8c8c' }}>
                Inga produkter
              </div>
            ) : (
              rankedItems.map((item, index) => (
                <ProductRankingCard
                  key={item.id}
                  item={item}
                  rank={pinnedItems.length + (rankingPage - 1) * rankingPageSize + index + 1}
                  onPin={onPin}
                />
              ))
            )}
          </div>

          <Pagination
            current={rankingPage}
            pageSize={rankingPageSize}
            total={rankingTotal}
            showSizeChanger
            pageSizeOptions={['20', '50', '100']}
            showTotal={(total, range) => `Visar ${range[0]}-${range[1]} av ${total} produkter`}
            style={{ textAlign: 'center', marginTop: '1rem', marginBottom: '0.5rem' }}
            onChange={onRankingPageChange}
          />
        </Spin>
      </div>
    </div>
  );
};
